import { useEffect, useState } from "react";
import {
  Box, Typography, Switch, FormControlLabel, Button, CircularProgress, Alert,
  TextField, FormControl, InputLabel, Select, MenuItem, Divider,
} from "@mui/material";
import Swal from "sweetalert2";
import {
  GetParcelMonitoringSettings, UpdateParcelMonitoringSettings,
  SendParcelMonitoringTestDigest, RunParcelMonitoringSweep, GetWhatsAppGroupChats,
} from "api/parcelMonitoring";
import { getAllCustomerGroups } from "api/customerGroup";

// Admin settings for the parcel-monitoring sweep: global default dwell/
// transit/unassigned thresholds (per-station overrides live on the Stations
// form) and the optional WhatsApp daily digest target.
const ParcelMonitorSettings = () => {
  const [config, setConfig] = useState(null); // null = loading
  const [instances, setInstances] = useState([]);
  const [groupChats, setGroupChats] = useState([]);
  const [loadingChats, setLoadingChats] = useState(false);
  const [chatsError, setChatsError] = useState(null); // real backend error, distinct from "genuinely empty"
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [runningSweep, setRunningSweep] = useState(false);
  const [reminderDaysText, setReminderDaysText] = useState("");

  useEffect(() => {
    (async () => {
      const [settingsRes, groupsRes] = await Promise.all([
        GetParcelMonitoringSettings(),
        getAllCustomerGroups(),
      ]);
      if (settingsRes.success) {
        setConfig(settingsRes.data);
        setReminderDaysText((settingsRes.data.customerReminder?.days || []).join(", "));
      }
      else {
        Swal.fire({ icon: "error", text: settingsRes.message || "Failed to load settings", timer: 2500, toast: true, position: "top-right", showConfirmButton: false });
      }
      if (groupsRes.success) setInstances(groupsRes.data || []);
    })();
  }, []);

  const selectedInstance = instances.find((i) => i.id === config?.whatsapp?.instanceId);
  const instanceNotReady = !!selectedInstance && selectedInstance.instanceStatus?.clientState !== "ready";

  useEffect(() => {
    setChatsError(null);
    if (!config?.whatsapp?.instanceId) {
      setGroupChats([]);
      return;
    }
    // Don't even attempt to list groups for a disconnected instance — the
    // WhatsApp client isn't there to ask, and the resulting error would
    // otherwise be indistinguishable from "no groups yet".
    if (instanceNotReady) {
      setGroupChats([]);
      return;
    }
    setLoadingChats(true);
    GetWhatsAppGroupChats(config.whatsapp.instanceId).then((res) => {
      setLoadingChats(false);
      if (res.success) {
        setGroupChats(res.data || []);
      } else {
        // Surface the REAL failure (client not initialized, page-evaluation
        // failed, etc.) instead of silently discarding it — an empty list from
        // a real error looks identical to a genuinely empty list otherwise,
        // and staff can't tell "add the number to the group" from "the
        // instance is broken" without this.
        setGroupChats([]);
        setChatsError(res.message || "Failed to load groups for this instance");
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config?.whatsapp?.instanceId, instanceNotReady]);

  const setField = (field, value) => setConfig((prev) => ({ ...prev, [field]: value }));
  const setWaField = (field, value) =>
    setConfig((prev) => ({ ...prev, whatsapp: { ...prev.whatsapp, [field]: value } }));
  const setReminderField = (field, value) =>
    setConfig((prev) => ({ ...prev, customerReminder: { ...prev.customerReminder, [field]: value } }));
  const setReminderTemplate = (lang, value) =>
    setConfig((prev) => ({
      ...prev,
      customerReminder: { ...prev.customerReminder, templates: { ...prev.customerReminder.templates, [lang]: value } },
    }));

  const save = async () => {
    const reminderDays = reminderDaysText.split(",").map((d) => d.trim()).filter(Boolean).map(Number);
    if (reminderDays.length === 0 || reminderDays.some((d) => !Number.isInteger(d) || d <= 0)) {
      Swal.fire({ icon: "warning", text: "Reminder days must be positive whole numbers, e.g. 3, 5, 7" });
      return;
    }
    setSaving(true);
    const res = await UpdateParcelMonitoringSettings({
      defaultDwellDays: config.defaultDwellDays,
      defaultTransitDays: config.defaultTransitDays,
      defaultUnassignedDays: config.defaultUnassignedDays,
      defaultTrackingPendingDays: config.defaultTrackingPendingDays,
      enabled: config.enabled,
      whatsapp: config.whatsapp,
      customerReminder: { ...config.customerReminder, days: reminderDays },
    });
    setSaving(false);
    Swal.fire({
      icon: res.success ? "success" : "error",
      text: res.success ? "Settings saved" : res.message || "Save failed",
      timer: 2000, toast: true, position: "top-right", showConfirmButton: false,
    });
  };

  const runNow = async () => {
    setRunningSweep(true);
    const res = await RunParcelMonitoringSweep();
    setRunningSweep(false);
    Swal.fire({
      icon: res.success ? "success" : "error",
      text: res.success
        ? `Sweep complete — ${res.data.violations} active, ${res.data.newlyOpened} new, ${res.data.resolved} resolved`
        : res.message || "Sweep failed",
      timer: 3000, toast: true, position: "top-right", showConfirmButton: false,
    });
  };

  const sendTest = async () => {
    if (!config?.whatsapp?.instanceId || !config?.whatsapp?.chatId) return;
    setTesting(true);
    const res = await SendParcelMonitoringTestDigest(config.whatsapp.instanceId, config.whatsapp.chatId);
    setTesting(false);
    Swal.fire({
      icon: res.success ? "success" : "error",
      text: res.success ? "Test message queued — check the group in WhatsApp" : res.message || "Failed to send",
      timer: 2500, toast: true, position: "top-right", showConfirmButton: false,
    });
  };

  if (config === null) {
    return <Box sx={{ p: 3 }}><CircularProgress color="secondary" /></Box>;
  }

  return (
    <Box sx={{ maxWidth: 560, py: 2 }}>
      <Typography variant="h6" color="secondary" sx={{ mb: 1 }}>
        Default dwell thresholds
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Business days (Sundays excluded). Used when a station has no per-station override — set those on the Stations form.
      </Typography>

      <FormControlLabel
        control={<Switch color="secondary" checked={!!config.enabled} onChange={(e) => setField("enabled", e.target.checked)} />}
        label="Parcel monitoring enabled"
        sx={{ mb: 2 }}
      />

      <Box sx={{ display: "flex", gap: 2, mb: 3, flexWrap: "wrap" }}>
        <TextField
          label="Stuck at station (days)" type="number" size="small"
          value={config.defaultDwellDays}
          onChange={(e) => setField("defaultDwellDays", Number(e.target.value))}
        />
        <TextField
          label="Missing in transit (days)" type="number" size="small"
          value={config.defaultTransitDays}
          onChange={(e) => setField("defaultTransitDays", Number(e.target.value))}
        />
        <TextField
          label="Not sorted / no route (days)" type="number" size="small"
          value={config.defaultUnassignedDays}
          onChange={(e) => setField("defaultUnassignedDays", Number(e.target.value))}
        />
        <TextField
          label="Tracking number pending (days)" type="number" size="small"
          value={config.defaultTrackingPendingDays}
          onChange={(e) => setField("defaultTrackingPendingDays", Number(e.target.value))}
        />
      </Box>

      <Button
        variant="outlined" color="secondary" sx={{ mb: 3 }}
        disabled={runningSweep}
        onClick={runNow}
      >
        {runningSweep ? <CircularProgress size={18} color="inherit" /> : "Run sweep now"}
      </Button>
      <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: -2, mb: 3 }}>
        Runs immediately instead of waiting for the nightly 03:30 job. Uses the last saved settings —
        save first if you changed the thresholds above.
      </Typography>

      <Divider sx={{ mb: 2 }} />

      <Typography variant="h6" color="secondary" sx={{ mb: 1 }}>
        WhatsApp daily digest
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Sends one summary message per day to a group chat. The phone number behind the selected
        instance must already be a member of that group in WhatsApp, or it will not appear below.
      </Typography>

      <FormControlLabel
        control={<Switch color="secondary" checked={!!config.whatsapp?.enabled} onChange={(e) => setWaField("enabled", e.target.checked)} />}
        label="Send WhatsApp digest"
        sx={{ mb: 2 }}
      />

      <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mb: 2, maxWidth: 400 }}>
        <FormControl size="small">
          <InputLabel>WhatsApp instance</InputLabel>
          <Select
            label="WhatsApp instance"
            value={config.whatsapp?.instanceId || ""}
            onChange={(e) => {
              setWaField("instanceId", e.target.value);
              setWaField("chatId", null);
              setWaField("chatName", null);
            }}
          >
            {instances.map((g) => (
              <MenuItem key={g.id} value={g.id}>{g.name}</MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl size="small" disabled={!config.whatsapp?.instanceId || loadingChats}>
          <InputLabel>Target group</InputLabel>
          <Select
            label="Target group"
            value={config.whatsapp?.chatId || ""}
            onChange={(e) => {
              const chat = groupChats.find((c) => c.id === e.target.value);
              setWaField("chatId", e.target.value);
              setWaField("chatName", chat?.name || null);
            }}
          >
            {groupChats.map((c) => (
              <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
            ))}
          </Select>
        </FormControl>

        {loadingChats && <Typography variant="caption" color="text.secondary">Loading groups…</Typography>}
        {!loadingChats && config.whatsapp?.instanceId && instanceNotReady && (
          <Alert severity="error">
            This instance isn't connected (status: {selectedInstance?.instanceStatus?.clientState || "unknown"}).
            Connect it in WhatsApp Settings before it can list or send to any group.
          </Alert>
        )}
        {!loadingChats && config.whatsapp?.instanceId && !instanceNotReady && chatsError && (
          <Alert severity="error">
            Couldn't load groups: {chatsError}
          </Alert>
        )}
        {!loadingChats && config.whatsapp?.instanceId && !instanceNotReady && !chatsError && groupChats.length === 0 && (
          <Alert severity="warning">
            No groups found for this instance. Make sure the instance's WhatsApp number has been added
            to the target group first.
          </Alert>
        )}
      </Box>

      <Button
        variant="outlined" color="secondary" sx={{ mb: 3 }}
        disabled={testing || !config.whatsapp?.instanceId || !config.whatsapp?.chatId}
        onClick={sendTest}
      >
        {testing ? <CircularProgress size={18} color="inherit" /> : "Send test message"}
      </Button>

      <Divider sx={{ mb: 2 }} />

      <Typography variant="h6" color="secondary" sx={{ mb: 1 }}>
        Customer pickup reminder
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Sends a WhatsApp reminder to the customer when a parcel is still waiting at a final station
        (not home delivery). Goes out at 09:00, Monday–Saturday, on exactly the business days listed
        below (Sundays excluded), from the customer group's WhatsApp instance. Parcels for the same
        customer and station are combined into one message.
      </Typography>

      <FormControlLabel
        control={
          <Switch
            color="secondary"
            checked={!!config.customerReminder?.enabled}
            onChange={(e) => setReminderField("enabled", e.target.checked)}
          />
        }
        label="Send pickup reminders"
        sx={{ mb: 2 }}
      />

      <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mb: 3 }}>
        <TextField
          label="Remind on business day(s)" size="small" sx={{ maxWidth: 260 }}
          value={reminderDaysText}
          onChange={(e) => setReminderDaysText(e.target.value)}
          helperText="Comma-separated, e.g. 3, 5, 7"
        />
        {[
          ["lo", "Message — Lao (also used when the customer has no language set)"],
          ["th", "Message — Thai"],
          ["en", "Message — English"],
        ].map(([lang, label]) => (
          <TextField
            key={lang} label={label} size="small" multiline minRows={4}
            value={config.customerReminder?.templates?.[lang] || ""}
            onChange={(e) => setReminderTemplate(lang, e.target.value)}
          />
        ))}
        <Typography variant="caption" color="text.secondary">
          Placeholders: {"{name}"} customer name, {"{station}"} branch name, {"{parcels}"} list of tracking numbers with days waiting.
        </Typography>
      </Box>

      <Box>
        <Button variant="contained" color="secondary" onClick={save} disabled={saving}>
          {saving ? <CircularProgress size={20} color="inherit" /> : "Save settings"}
        </Button>
      </Box>
    </Box>
  );
};

export default ParcelMonitorSettings;
